import { Button, ButtonStyle, Markdown } from '@davidlloydleisure/common-js-frontend';
import classNames from 'classnames';
import React, { use, useRef, useState } from 'react';

import { ClubSearchLink } from 'src/components/common/club-search/club-search-link';
import { RecentlyVisitedClubInfo } from 'src/components/common/recently-visited-club-info';
import Cross from 'src/components/icons/cross';
import Map from 'src/components/icons/map';
import Search from 'src/components/icons/search-icon';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import { useClubSearch, useClubSearchForm } from 'src/custom-hooks/use-club-search-form';
import {
  amplitudeBrandName,
  amplitudeButtonTrackEvent,
  amplitudeTrackEvent,
} from 'src/helpers/amplitude-event-track';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import { onMarkdownLinkClick } from 'src/helpers/markdown-helper';

import HeaderMobileSubMenuWrapper from '../header-mobile/header-sub-menu-wrapper';

import * as style from './header-club-search.module.scss';

type HeaderClubSearchProps = {
  isMenuOpen: boolean;
  isVisible: boolean;
  openClubSearch?: () => void;
  onFinish?: () => void;
};

const HeaderClubSearch = (props: HeaderClubSearchProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { styling, pageLevel, buildType, language } = use(SiteContext);
  const { headerContent } = use(GlobalCmsContent);
  const [searchValue, setSearchValue] = useState<string>('');
  const handleSubmit = useClubSearchForm(inputRef, true, props.onFinish);
  const filteredClubs = useClubSearch(searchValue);

  if (!headerContent.searchSettings) {
    return null;
  }

  const handleInputFocus = () => {
    void amplitudeTrackEvent(`Focus Input`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      ctaName: 'Focus to Club List in the "Find a club" - Navigation',
      component: 'Header Club Search',
    });
  };

  const handleClickViewMap = () => {
    void amplitudeButtonTrackEvent({
      buildType,
      language,
      button: null,
      componentName: 'Header Club Search',
      ctaName: 'View Map in the "Find a club" - Navigation',
      pageLevel,
    });
  };

  const viewMapLink = (
    <button type="submit" className={style.viewMapButton} onClick={handleClickViewMap}>
      <Map />
      {headerContent.searchSettings.mobilePinnedSearchOptionText}
    </button>
  );

  const brand = getBrandForBuildType(buildType);

  return (
    <HeaderMobileSubMenuWrapper
      isMenuOpen={props.isMenuOpen}
      isVisible={props.isVisible}
      isChildMenuVisible={false}
    >
      <div>
        <div>
          <div className={style.title}>{headerContent.searchSettings.title}</div>
          <Markdown
            markdownText={headerContent.searchSettings.description}
            colorOverride={styling.colors.capeCod}
            brand={brand}
            onLinkClick={onMarkdownLinkClick(buildType, language, 'Club Search')}
            classNameToOverride={style.clubSearchText}
          />
        </div>

        <form onSubmit={handleSubmit} className={style.formContainer}>
          <div className={style.wrapper}>
            <div className={style.inputWrapper}>
              {searchValue ? (
                <div
                  tabIndex={0}
                  onClick={() => {
                    setSearchValue('');
                    inputRef.current?.focus();
                  }}
                  className={style.clearButton}
                >
                  <Cross />
                </div>
              ) : null}
              <div className={style.searchIcon}>
                <Search fill={styling.colors.capeCod} />
              </div>
              <input
                ref={inputRef}
                value={searchValue}
                onChange={(e) => {
                  const { value } = e.currentTarget;
                  setSearchValue(value);
                }}
                name="club-search"
                autoComplete="off"
                autoCorrect="off"
                enterKeyHint="search"
                placeholder={headerContent.searchSettings.searchPlaceholderMobile}
                className={style.input}
                onFocus={() => handleInputFocus()}
              />
            </div>
            <Button
              brand={brand}
              text={headerContent.searchSettings.mobileSearchButtonName}
              style={ButtonStyle.PRIMARY}
              className={classNames(style.headerClubSearchMobile)}
              isSubmit
            />
          </div>
          <div className={style.dropdown}>
            {searchValue && viewMapLink}
            {searchValue &&
              filteredClubs.map((club) => (
                <ClubSearchLink key={club.siteId} club={club} onClick={props.onFinish} />
              ))}
          </div>
        </form>
        <RecentlyVisitedClubInfo />
      </div>
    </HeaderMobileSubMenuWrapper>
  );
};

export default HeaderClubSearch;
